import React from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const ProgressAnalytics = ({ analyticsData }) => {
  const StatCard = ({ icon, label, value, trend, trendDirection }) => (
    <div className="bg-card border border-border rounded-md p-4 md:p-6">
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-md bg-primary/10 flex items-center justify-center">
          <Icon name={icon} size={20} color="var(--color-primary)" />
        </div>
        {trend && (
          <div className={`flex items-center space-x-1 text-xs font-medium ${trendDirection === 'up' ? 'text-success' : 'text-error'}`}>
            <Icon name={trendDirection === 'up' ? 'TrendingUp' : 'TrendingDown'} size={14} color="currentColor" />
            <span>{trend}</span>
          </div>
        )}
      </div>
      <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-1">
        {value}
      </p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon="Calendar"
          label="Total Encounters"
          value={analyticsData?.totalEncounters}
          trend="+12%"
          trendDirection="up"
        />
        <StatCard
          icon="CheckCircle"
          label="Completion Rate"
          value={`${analyticsData?.completionRate}%`}
          trend="+8%"
          trendDirection="up"
        />
        <StatCard
          icon="Heart"
          label="Avg Satisfaction"
          value={analyticsData?.avgSatisfaction?.toFixed(1)}
          trend="+0.5"
          trendDirection="up"
        />
        <StatCard
          icon="Target"
          label="Fulfillment Score"
          value={`${analyticsData?.fulfillmentScore}%`}
          trend="+15%"
          trendDirection="up"
        />
      </div>
      <div className="bg-card border border-border rounded-md p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              Satisfaction Trends
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Last 6 months performance
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="TrendingUp" size={20} color="var(--color-success)" />
          </div>
        </div>
        <div className="w-full h-64 md:h-80" aria-label="Satisfaction trends line chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={analyticsData?.satisfactionTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
              />
              <Line 
                type="monotone" 
                dataKey="satisfaction" 
                stroke="var(--color-primary)" 
                strokeWidth={2}
                name="Satisfaction"
                dot={{ fill: 'var(--color-primary)', r: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="communication" 
                stroke="var(--color-accent)" 
                strokeWidth={2}
                name="Communication"
                dot={{ fill: 'var(--color-accent)', r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-card border border-border rounded-md p-4 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground">
              Encounter Types Distribution
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Breakdown by preference category
            </p>
          </div>
        </div>
        <div className="w-full h-64 md:h-80" aria-label="Encounter types bar chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={analyticsData?.encounterTypes}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="type" 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Bar 
                dataKey="count" 
                fill="var(--color-primary)" 
                radius={[6, 6, 0, 0]}
                name="Count"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/10 rounded-md p-4 md:p-6">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Sparkles" size={20} color="var(--color-primary)" />
          </div>
          <div className="flex-1">
            <h4 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2">
              AI Insights
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {analyticsData?.aiInsight}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressAnalytics;